import { useEffect, useMemo, useState } from "react";

import type { DisplayPayload } from "../store/useCampaignDetailStore";

// Interval (ms) used to cycle through a Box's ready auto-previews.
export const PREVIEW_CYCLE_INTERVAL_MS = 3000;

export interface ResolvedPreview {
  imageUrl: string | null; // URL to render, or null to fall back to the placeholder
  /**
   * Always false. Kept so the shape (and its consumers) stay unchanged, but a
   * Box now resolves synchronously: there is either an image to show or there
   * is not.
   */
  isLoading: boolean;
}

/**
 * Resolve what a Box should display from its display_payload.
 *
 * Image if one exists, placeholder otherwise — no intermediate loading state.
 * A `pending` preview is treated as "no image yet": the generation job reports
 * only by writing to the database, and the app has neither polling nor event
 * listening, so a spinner shown here would never clear on its own.
 *
 * Still cycles through multiple ready previews on a timer, and still honours
 * the manual-over-auto priority rule.
 */
export const useResolvedPreview = (
  displayPayload?: DisplayPayload,
): ResolvedPreview => {
  // The ordered list of renderable, ready preview URLs we cycle through.
  const readyUrls = useMemo<string[]>(() => {
    if (!displayPayload || displayPayload.source !== "auto") return [];
    return displayPayload.auto_previews
      .filter((entry) => entry.status === "ready" && !!entry.url)
      .map((entry) => entry.url as string);
  }, [displayPayload]);

  const [cycleIndex, setCycleIndex] = useState(0);

  useEffect(() => {
    // Reset to the first entry whenever the set of ready previews changes.
    setCycleIndex(0);

    // A single entry (or none) renders statically — only cycle with >1 entries.
    if (readyUrls.length <= 1) return;

    const intervalId = setInterval(() => {
      setCycleIndex((prev) => (prev + 1) % readyUrls.length);
    }, PREVIEW_CYCLE_INTERVAL_MS);

    // Clear on unmount and whenever the ready list changes to avoid leaks.
    return () => clearInterval(intervalId);
  }, [readyUrls]);

  // source=manual -> render the manual image, no cycling.
  if (displayPayload?.source === "manual") {
    return { imageUrl: displayPayload.manual_image?.url ?? null, isLoading: false };
  }

  // source=auto -> cycle ready entries; otherwise reflect pending/failed state.
  if (displayPayload?.source === "auto") {
    if (readyUrls.length > 0) {
      const safeIndex = cycleIndex % readyUrls.length;
      return { imageUrl: readyUrls[safeIndex], isLoading: false };
    }

    // Nothing ready: fall straight through to whatever url is available, or to
    // the placeholder. Pending gets no loading state — the generation job
    // reports only by writing to the database and the app neither polls nor
    // listens for events, so a spinner here could never resolve on its own.

    // Otherwise surface any entry that still carries a renderable url. This
    // looks past `failed` on purpose: a `ready` entry whose url is null (the
    // generator recorded success but produced no key, or signing failed) is
    // dropped by the readyUrls filter above and would otherwise be reported as
    // a failure. Matching on the url rather than the status means whatever is
    // actually displayable gets displayed.
    const anyWithUrl = displayPayload.auto_previews.find((entry) => !!entry.url);
    return { imageUrl: anyWithUrl?.url ?? null, isLoading: false };
  }

  // source=none or no payload -> caller falls back to the placeholder.
  return { imageUrl: null, isLoading: false };
};
